import { ReactNode } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { Icon } from '@iconify/react';
import { Overline } from 'components/InterviewResults';

type Props = {
  open: boolean;
  /** Вызывается по кнопке/закрытию — родитель закрывает окно и запрашивает доступ к устройствам. */
  onConfirm: () => void;
};

type FeatureRowProps = {
  icon: string;
  color: string;
  title: string;
  description: ReactNode;
};

const FeatureRow = ({ icon, color, title, description }: FeatureRowProps) => (
  <Stack direction="row" spacing={2} alignItems="flex-start">
    <Box
      sx={{
        flexShrink: 0,
        width: 40,
        height: 40,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(color, 0.12),
        color
      }}
    >
      <Icon icon={icon} width={22} height={22} />
    </Box>
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Stack>
);

/**
 * Вступительное окно перед проверкой устройств. Объясняет, что тестирование проходит
 * в формате видео-интервью с ИИ, из каких частей оно состоит, зачем нужны камера и
 * микрофон, и предупреждает, что следующим шагом браузер запросит к ним доступ.
 */
const InterviewIntroModal = ({ open, onConfirm }: Props) => {
  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onConfirm} scroll="body" aria-labelledby="interview-intro-title">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
          <Typography textAlign="center" variant="h6">
            Видео-интервью с ИИ
          </Typography>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main'
            }}
          >
            <Icon icon="mdi:robot-happy-outline" width={32} height={32} />
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack spacing={5}>
          {/* Заголовок */}
          <Stack spacing={1.5} alignItems="center" textAlign="center">
            <Typography sx={{ mt: '16px !important' }} variant="body2" color="text.secondary">
              Тестирование проходит в формате собеседования, вопросы
              искусственный интеллект. Отвечайте голосом, глядя в камеру — как на обычном интервью.
            </Typography>
          </Stack>

          {/* Как всё устроено */}
          <Box>
            <Overline>
              Как всё устроено
            </Overline>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FeatureRow
                icon="mdi:briefcase-outline"
                color={theme.palette.primary.main}
                title="Этап 1"
                description="Несколько вопросов о вашем опыте."
              />
              <FeatureRow
                icon="mdi:head-cog-outline"
                color={theme.palette.primary.main}
                title="Этап 2"
                description="Вопросы о профессиональных навыках и о личностных качествах."
              />
            </Stack>
          </Box>

          {/* Зачем доступ */}
          <Box>
            <Overline>
              Доступ к устройствам
            </Overline>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FeatureRow
                icon="mdi:video-outline"
                color={theme.palette.info.main}
                title="Камера"
                description="В первую очередь — чтобы считывать ваши эмоции и оценить личностные качества."
              />
              <FeatureRow
                icon="mdi:microphone-outline"
                color={theme.palette.info.main}
                title="Микрофон"
                description="Записывает ваши ответы для последующего разбора."
              />
            </Stack>
          </Box>

          {/* Безопасность / следующий шаг */}
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.24)}`
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ color: 'success.main', mt: '2px', flexShrink: 0 }}>
                <Icon icon="mdi:shield-check-outline" width={22} height={22} />
              </Box>
              <Typography variant="body1" color="text.secondary">
                Следующим шагом браузер попросит доступ к камере и микрофону — пожалуйста, разрешите
                его. Это полностью безопасно: доступ можно отозвать в любой момент, а запись идёт
                только пока вы находитесь на странице интервью, и прекращается, как только вы её
                покинете.
              </Typography>
            </Stack>
          </Box>

          <Button size="large" fullWidth variant="contained" onClick={onConfirm}>
            Понятно, продолжить
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default InterviewIntroModal;